import React, { FunctionComponent, useState } from 'react';
import { CardCTASection } from './CardCTASection';
import { PromotionAvailableBlock } from '../PromotionAvailableBlock';
import { ParsedPromo } from '../types';
import dompurify from 'dompurify';
import { Heading } from '../../../shared/Heading';
import { Paragraph } from '../../../shared/Paragraph';
import { joinStrings, filterSpecialHTMLCharacters, replaceSpecialSymbols } from '../../../../utils/string';
import pandaMoneyImg from '../../../../assets/Games/PandaMoney.png';
import { formatDate } from 'date-fns';
import { PromotionMessagesPrompt } from '../PromotionMessagesPrompt';
import { CardActiveCTASection } from './CardActiveCTASection';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import './styles';

interface PromotionCardProps {
  isActivePromotions?: boolean;
  promo: ParsedPromo;
  withoutMargin?: boolean;
  showCTASection?: boolean;
  showMoreButon?: boolean;
}

export const PromotionCard: FunctionComponent<PromotionCardProps> = ({
  promo,
  withoutMargin,
  showCTASection = true,
  isActivePromotions,
  showMoreButon = true,
}): JSX.Element => {
  const sanitizer = dompurify.sanitize;
  const { t } = useTranslations();

  const {
    title,
    description,
    termsDescr,
    imgPath,
    imgAlt,
    promoButtonText,
    pageUrl,
    endDate,
    expireDateLabel,
    wageredAmount,
    wageringRequirementAmount,
  } = promo;

  const dateLabel = expireDateLabel || (endDate ? formatDate(endDate, 'dd/MM/yy') : undefined);

  const [showPromoPrompt, setShowPromoPrompt] = useState<boolean>(false);

  return (
    <>
      <div
        className={joinStrings([
          'promotion-card',
          withoutMargin && 'promotion-card--specific promotion-card--without-margin',
        ])}
      >
        <div className="promotion-card__banner-block">
          <img src={`${imgPath || pandaMoneyImg}`} alt={imgAlt} />
          {Boolean(dateLabel) && <PromotionAvailableBlock expireDateTag={String(dateLabel)} />}
        </div>
        <div className="promotion-card__info-block">
          <div className="promotion-card__description-block padding-16">
            <Heading className="promotion-card__banner-title" level={6}>
              {title}
            </Heading>
            {Boolean(description) && (
              <>
                <Paragraph
                  className="promotion-card__description"
                  noMargin
                  dangerouslySetInnerHTML={{
                    __html: sanitizer(description),
                  }}
                />
                <Paragraph noMargin className="promotion-card__short-terms">
                  {termsDescr && replaceSpecialSymbols(filterSpecialHTMLCharacters(sanitizer(termsDescr)), true)}{' '}
                </Paragraph>
              </>
            )}
          </div>
          {showCTASection &&
            (isActivePromotions ? (
              <CardActiveCTASection
                depositedValue={Number(wageredAmount)}
                remainingValue={Number(wageringRequirementAmount)}
                pageUrl={pageUrl}
                showButton={showMoreButon}
                activatedPromo={promo}
              />
            ) : (
              <CardCTASection
                onActivateBonus={(): void => setShowPromoPrompt(true)}
                buttonText={promoButtonText}
                pageUrl={pageUrl}
                promotionCTAType={promo.promoType === 'depositMatch' ? '' : 'NoCTA'}
              />
            ))}
        </div>
      </div>
      <PromotionMessagesPrompt
        setShow={setShowPromoPrompt}
        show={showPromoPrompt}
        paragraphText={t('promotions.claim.text')}
        ctaTextSecondary={t('buttons.cancel.uppercase')}
        onClose={(): void => setShowPromoPrompt(false)}
        activatedPromo={promo}
      />
    </>
  );
};
